/* eslint-disable import/no-named-default */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useRef, useState, useMemo } from 'react';
import JsonQuery from 'json-query';
import { Dropdown } from 'react-bootstrap';
import { useLocation } from 'react-router';
import {
    hooks as commonHooks,
    types as commonTypes,
    analytics as commonAnalytics,
    annotations as commonAnnotations,
    components as commonComponents,
    utils as commonUtils,
} from '@sitebuilder/common';
import { types as repr } from '@sitebuilder/core';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import dropdownWithCustomQueryStyle from './DropdownWithCustomQuery.styles';

interface DropdownList {
    isOpen?: boolean;
    listData?: any;
}

interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}

interface DropdownWithCustomQueryProps {
    label?: string;
    ariaLabel?: string;
    dataKey?: string;
    itemDataKey?: string;
    targetTop?: boolean;
    topURLDataKey?: string;
    placeholder?: string;
    loadingText?: string;
    iconPosition?: string;
    linkDataKey?: string;
    iconType?: string;
    dropdownCloseIcon?: string;
    dropdownOpenIcon?: string;
    dropdownCloseImage?: string;
    dropdownOpenImage?: string;
    showSearch?: boolean;
    noOptionFoundText?: string;
    searchPlaceholder?: string;
    searchIconPosition?: string;
    searchIconType?: string;
    searchIcon?: string;
    searchImageUrl?: string;
    searchCancelIcon?: string;
    searchCancelImageUrl?: string;
    transformData?: boolean;
    functionToTransformData?: string;
    typographyClass?: string;
    useCustomQuery?: boolean;
    dataSourceType?: string;
    dataSource?: string;
    query?: string;
    queryVariables?: string;
    transform?: string;
    hideDDIconSearch?: boolean;
    useAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
    useCustomUrl?: boolean;
    customUrl?: string;
    autoSearchFocus?: boolean;
    useLowerCase?: boolean;
}
export interface WidgetProperties
    extends commonTypes.WidgetPropertiesBase,
        DropdownWithCustomQueryProps {}

const DropdownWithCustomQuery: React.FC<commonTypes.WidgetInstanceProps> = (props) => {
    const appContext = commonHooks.useAppContext();
    const { graphQLClient } = appContext;
    const { data } = props;
    const i18n = commonHooks.useLocalization();
    const tracking = commonAnalytics.useTracking();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const widgetInstances = appContext.document?.widgetInstances;
    const widgetInstance = ((props.widgetInstanceId && widgetInstances) || {})[
        props.widgetInstanceId || ''
    ];
    const { styles, useAnalytics, dynamicEvents, useCustomUrl, useLowerCase, ...rest } =
        widgetProperties || props;
    const theme = data?.themeSelected || props?.theme;
    const [queryResult, setQueryResult] = useState<repr.GraphQLQueryResult | null>(null);
    const [dropdownList, setDropdownList] = useState<DropdownList>({});
    const [searchValue, setSearchValue] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [showSearch, setShowSearch] = useState(false);
    // store the filtered indices
    const [filteredIndices, setFilteredIndices] = useState<number[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownToggleRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchContainerRef = useRef<any>(null);
    const { pathname } = useLocation();

    const toggleDropdown = (_isOpen?: any) => {
        setIsOpen(_isOpen !== undefined ? _isOpen : !isOpen);
        setShowSearch(!showSearch);
    };

    const toggleDropdownOnKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.click();
            toggleDropdown();
        }
    };

    // handle if click outside of drodown and toggle icon
    const handleClickOutside = (e: MouseEvent) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(e.target as Node) &&
            dropdownToggleRef.current &&
            !dropdownToggleRef.current.contains(e.target as Node) &&
            searchInputRef.current &&
            !searchInputRef.current.contains(e.target as Node)
        ) {
            setIsOpen(false);
            setSearchValue('');
            setShowSearch(false);
        }
    };

    useEffect(() => {
        // event listener to check if click outside of component
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // remove the event listener
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        // This function will be called when the scroll event occurs
        const handleScroll = () => {
            // Close the dropdown by setting showDropdown to false
            setIsOpen(false);
        };

        // Add an event listener to the window object for the scroll event
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener when the component is unmounted
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const transform = widgetInstance?.properties?.transform as string;
    const extensions: Record<string, unknown> = {};

    if (transform) {
        extensions.transform = transform;
    }

    const variablesStr = rest?.queryVariables as string;
    let variables = '';

    try {
        const transformFunctionName = `${props.widgetInstanceId}-queryVariables-transform`;

        if (typeof variablesStr === 'string' && variablesStr.length > 0) {
            variables = commonUtils.transformDataWithGlobals(
                transformFunctionName,
                variablesStr,
                data
            );
        }
    } catch (e) {
        console.log(e);
    }

    const originalHostname = appContext.originalHostname;
    const baseUrl = appContext.baseUrl || window.location.origin;
    const queryParams = {
        dataSourceType: rest?.dataSourceType as string,
        dataSetName: rest?.dataSource as string,
        authorizationToken: appContext.accessToken as string,
        query: rest?.query as string,
        variables,
        extensions: extensions as Record<string, unknown>,
        originalHostname,
        baseUrl,
    };

    const queryId = commonUtils.hashObject(queryParams as Record<string, unknown>);

    let isRender: any = {};

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const _transformedData =
            rest.transformData &&
            commonUtils.transformDataWithGlobals(
                transformFunctionName,
                rest.functionToTransformData,
                {
                    data,
                    JsonQuery,
                    ...isRender,
                }
            );

        if (_transformedData) {
            isRender = { ...isRender, ..._transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    let listData: any = useMemo(() => {
        if (!rest.useCustomQuery) {
            const tmp =
                JsonQuery(rest?.dataKey as string, {
                    data: { ...data, ...isRender },
                })?.value || [];
            return tmp;
        } else {
            return null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, rest.dataKey, rest.useCustomQuery]);

    listData = useMemo(() => {
        if (rest.useCustomQuery) {
            const tmp = queryResult?.data as Record<string, unknown>;
            return tmp
                ? JsonQuery(rest?.dataKey as string, {
                      data: { ...data, ...tmp },
                  })?.value || []
                : null;
        } else {
            return listData;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryResult?.data, rest.dataKey]);

    const loadQueryData = async (graphqlQueryParams: any) => {
        try {
            const queryApiClient = graphQLClient;
            const graphQLQueryResult = await queryApiClient.executeQuery(graphqlQueryParams);

            if (graphQLQueryResult?.data && Array.isArray(graphQLQueryResult.data.annotations)) {
                appContext.setters.setAnnotations(
                    commonAnnotations.annotator.Annotator.addAnnotations(
                        graphQLQueryResult.data.annotations,
                        appContext.annotations
                    )
                );
            }

            return graphQLQueryResult;
        } catch (err: any) {
            console.error(err);
            return null;
        }
    };

    commonHooks.useSSE2.useSSE(async () => {
        if (isOpen && !listData && !queryResult && rest.useCustomQuery) {
            await loadQueryData(queryParams).then((result) => setQueryResult(result));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, queryId, widgetProperties]);

    const updateURL = (dropdown: DropdownList): void => {
        const selectedOption = dropdown.listData?.find((ld: any) => ld?.isSelected);

        tracking({
            event: 'Selected Fund Name',
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            fundName: selectedOption?.fundName,
            useLowerCase: useLowerCase,
        });

        const id = i18n.format(rest?.linkDataKey || '', selectedOption);
        // check if page is within iframe
        if (rest.targetTop && appContext.isClient && window?.self !== window?.top) {
            const topURLDataKey = i18n.format(rest.topURLDataKey, data);
            const topUrlData = JsonQuery(`${topURLDataKey}`, { data })?.value;
            const url = topUrlData.find((d: any) => d?.entityId?.includes(id))?.url;
            // navigate to parent window
            if (window.top) window.top.location.href = url;
        } else if (useCustomUrl && rest?.customUrl) {
            // use custom url
            const URLDataKey = i18n.format(rest.customUrl, data);
            const UrlData = JsonQuery(`${URLDataKey}`, { data })?.value;
            const url = UrlData.find((d: any) => d?.entityId?.includes(id))?.url;
            window.location.href = url;
        } else {
            // navigate without iframe
            window.location.href = `${pathname}?profileId=${id}&locale=${data?.locale}`;
        }
    };

    const onClickItem = (listDataSelectedIndex: number) => {
        setDropdownList({
            ...dropdownList,
            listData: listData?.map((ld: any, i: number) =>
                i === listDataSelectedIndex
                    ? { ...ld, isSelected: true }
                    : { ...ld, isSelected: false }
            ),
        });

        updateURL({
            ...dropdownList,
            listData: listData?.map((ld: any, i: number) =>
                i === listDataSelectedIndex
                    ? { ...ld, isSelected: true }
                    : { ...ld, isSelected: false }
            ),
        });
    };

    const handleDropdownSearchKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Escape') {
            toggleDropdown(false);
        } else if (event.key === ' ') {
            // Prevent default behavior of spacebar
            event.preventDefault();
            // Add a space to the search input
            setSearchValue((prevSearch) => prevSearch + ' ');
        }
    };

    const renderIcon = () => {
        return (
            <>
                <>
                    {!(searchValue.length > 0 && rest?.hideDDIconSearch) &&
                        (rest?.iconType === 'FontAwesome Icon' ? (
                            <i
                                className={`dropdown-toggle-icon fas ${
                                    isOpen ? rest?.dropdownCloseIcon : rest?.dropdownOpenIcon
                                }`}
                                onClick={rest?.showSearch ? () => toggleDropdown() : () => null}
                                onKeyDown={
                                    rest?.showSearch
                                        ? (e) => toggleDropdownOnKeyDown(e)
                                        : () => null
                                }
                                data-testid={'dropdown-toggle-icon'}
                            />
                        ) : rest?.iconType === 'Image' ? (
                            <img
                                className={'dropdown-toggle-image'}
                                src={
                                    new URL(
                                        isOpen
                                            ? rest?.dropdownCloseImage || ''
                                            : rest?.dropdownOpenImage || '',
                                        baseUrl
                                    ).href
                                }
                                alt={isOpen ? 'dropdown-toggle-open' : 'dropdown-toggle-close'}
                                onClick={rest?.showSearch ? () => toggleDropdown() : () => null}
                                onKeyDown={
                                    rest?.showSearch
                                        ? (e) => toggleDropdownOnKeyDown(e)
                                        : () => null
                                }
                                data-testid={'dropdown-toggle-icon'}
                            />
                        ) : null)}
                </>
            </>
        );
    };

    const renderSearchIcon = () => {
        return (
            <>
                {rest?.searchIconType === 'FontAwesome Icon' ? (
                    <i className={`dropdown-search-icon fas ${rest?.searchIcon}`} />
                ) : rest?.searchIconType === 'Image' ? (
                    <img
                        className={'dropdown-search-image'}
                        src={rest?.searchImageUrl}
                        alt={'dropdown-search'}
                    />
                ) : null}
            </>
        );
    };

    const renderCancelIcon = () => {
        return (
            <>
                {rest?.searchIconType === 'FontAwesome Icon' ? (
                    <i
                        className={`dropdown-search-cancel-icon fas ${rest?.searchCancelIcon}`}
                        data-testid={'dropdown-search-cancel-icon'}
                        onClick={() => setSearchValue('')}
                    />
                ) : rest?.searchIconType === 'Image' ? (
                    <img
                        className={'dropdown-search-cancel-image'}
                        src={rest?.searchCancelImageUrl}
                        data-testid={'dropdown-search-cancel-icon'}
                        alt={'dropdown-search-cancel'}
                        onClick={() => setSearchValue('')}
                    />
                ) : null}
            </>
        );
    };

    const renderOptions = () => {
        const { itemDataKey } = rest;

        const filteredListData = filteredIndices.map((filteredIndex) => listData[filteredIndex]);

        return Array.isArray(filteredListData) && filteredListData.length > 0 ? (
            filteredListData?.map((item: any, index: number) => (
                <div
                    key={index}
                    role="option"
                    aria-selected={item.isSelected ? 'true' : 'false'}
                    tabIndex={0}
                    data-testid={`dropdown-option-${filteredIndices[index]}`} // Use filtered index
                    onClick={() => onClickItem(filteredIndices[index])}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            onClickItem(filteredIndices[index]); // Use filtered index
                        }
                    }}
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(itemDataKey || '', {
                            ...item,
                        }),
                    }}
                />
            ))
        ) : (
            <span className="no-option-found">{i18n.format(rest?.noOptionFoundText, data)}</span>
        );
    };

    // Update the filtered indices whenever the listData or searchValue changes
    useEffect(() => {
        const { itemDataKey } = rest;
        if (Array.isArray(listData)) {
            const newFilteredIndices = listData
                .map((item, index) => ({
                    item,
                    index,
                    lowerCaseText: i18n.format(itemDataKey || '', { ...item })?.toLowerCase(),
                }))
                .filter(
                    (item) => !searchValue || item.lowerCaseText.includes(searchValue.toLowerCase())
                )
                .map((item) => item.index);

            setFilteredIndices(newFilteredIndices);
        } else {
            setFilteredIndices([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listData, searchValue]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const options: any = Array.from(
            document.querySelectorAll('#custom-query-dropdown-label [role="option"]')
        );
        const currentIndex = options.findIndex((option: any) => option === document.activeElement);

        if (e.key === 'ArrowDown') {
            // Prevent default scrolling
            e.preventDefault();
            const nextIndex: any = (currentIndex + 1) % options.length;
            options?.[nextIndex]?.focus();
        } else if (e.key === 'ArrowUp') {
            // Prevent default scrolling
            e.preventDefault();
            const prevIndex: any = (currentIndex - 1 + options.length) % options.length;
            options[prevIndex]?.focus();
        } else if (e.key === 'Tab' && currentIndex !== -1) {
            // Prevent default tabbing out
            e.preventDefault();
            const nextIndex: any = (currentIndex + 1) % options.length;
            options[nextIndex]?.focus();
        }
    };

    return (
        <commonComponents.AppContext.AppContextProvider
            {...{
                ...appContext,
                queries: {
                    ...appContext.queries,
                    [queryId]: {
                        isQueryLoading: false,
                        graphqlQueryParams: queryParams,
                        graphQLQueryResult: queryResult,
                    },
                },
            }}
        >
            <div
                className={`dropdown-container ${widgetProperties?.typographyClass ?? ''}`}
                css={css`
                    ${theme ? dropdownWithCustomQueryStyle : ''}
                    ${getTextSchemaProperties(widgetProperties, data?.colorSpace)}
                ${getStyleSchemaProperties(widgetProperties)}
                ${getLayoutSchemaProperties(widgetProperties)}
                ${styles ?? ''}
                `}
                data-testid={'dropdown-container'}
            >
                <div
                    className="custom-query-dropdown-title"
                    id={`custom-query-dropdown-label-${props.widgetInstanceId}`}
                >
                    {i18n.format(rest?.label, data)}
                </div>
                <Dropdown
                    id="custom-query-dropdown-label"
                    ref={dropdownRef}
                    show={isOpen}
                    onKeyDown={handleKeyDown}
                    onToggle={(isOpen, e: any) => {
                        if (rest?.showSearch) {
                            if (
                                searchContainerRef?.current &&
                                !searchContainerRef?.current?.contains(e.target) &&
                                e.key !== 'Tab'
                            ) {
                                toggleDropdown(isOpen);
                            }
                            if (!showSearch && e.key !== 'Tab') toggleDropdown(true);
                        } else {
                            toggleDropdown(isOpen);
                        }
                    }}
                >
                    <Dropdown.Toggle
                        ref={dropdownToggleRef}
                        data-testid={'dropdown-toggle'}
                        aria-labelledby={`custom-query-dropdown-label-${props.widgetInstanceId}`}
                    >
                        {rest?.iconPosition === 'Left' && renderIcon()}
                        {rest?.showSearch && showSearch ? (
                            <div ref={searchContainerRef} className="dropdown-search-container">
                                {rest?.searchIconPosition === 'Left' &&
                                    (searchValue?.length > 0
                                        ? renderCancelIcon()
                                        : renderSearchIcon())}
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className="dropdown-search-input"
                                    placeholder={i18n.format(rest?.searchPlaceholder, data)}
                                    value={searchValue || ''}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    data-testid={'search-input'}
                                    onKeyDown={handleDropdownSearchKeyDown}
                                    autoFocus={isOpen && rest?.autoSearchFocus}
                                />
                                {rest?.searchIconPosition === 'Right' &&
                                    (searchValue?.length > 0
                                        ? renderCancelIcon()
                                        : renderSearchIcon())}
                            </div>
                        ) : (
                            <span className="placeholder" data-testid={'placeholder'}>
                                {i18n.format(rest?.placeholder, data)}
                            </span>
                        )}
                        {rest?.iconPosition === 'Right' && renderIcon()}
                    </Dropdown.Toggle>
                    {queryResult || listData ? (
                        <Dropdown.Menu data-testid={'dropdown-options'}>
                            {renderOptions()}
                        </Dropdown.Menu>
                    ) : (
                        <Dropdown.Menu data-testid={'dropdown-options'}>
                            <span className="loading-text">
                                {i18n.format(rest?.loadingText, data)}
                            </span>
                        </Dropdown.Menu>
                    )}
                </Dropdown>
            </div>
        </commonComponents.AppContext.AppContextProvider>
    );
};

export default DropdownWithCustomQuery;
